import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import * as XLSX from "xlsx";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AppShell } from "@/components/layout/app-shell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trash2, Plus, Save, FileSpreadsheet, Loader2, Printer } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";

const glidepathEntrySchema = z.object({
  ecmNumber: z.string().optional().default(""),
  initiative: z.string().min(1, "Initiative description is required"),
  status: z.string().min(1, "Status is required"),
  plan: z.string().min(1, "Plan is required"),
  stepsToComplete: z.string().min(1, "Steps to complete are required"),
  benefitVsCost: z.string().min(1, "Benefit vs Cost is required"),
  cashGeneration: z.string().min(1, "Cash generation is required"),
  comments: z.string().optional().default("")
});

const glidepathFormSchema = z.object({
  glidepathEntries: z.array(glidepathEntrySchema).default([])
});

type GlidepathFormValues = z.infer<typeof glidepathFormSchema>;

export default function OsemGlidepathPage() {
  const [, params] = useRoute("/osem-onboarding/:id/glidepath");
  const id = params?.id ? parseInt(params.id) : null;
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: proposal, isLoading } = useQuery<any>({
    queryKey: [id ? `/api/osem/${id}` : null].filter(Boolean),
    enabled: !!id
  });

  const form = useForm<GlidepathFormValues>({
    resolver: zodResolver(glidepathFormSchema),
    defaultValues: {
      glidepathEntries: []
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "glidepathEntries"
  });

  useEffect(() => {
    if (proposal) {
      if (proposal.glidepathEntries && Array.isArray(proposal.glidepathEntries) && proposal.glidepathEntries.length > 0) {
        replace(proposal.glidepathEntries);
      } else {
        // Pre-populate with default Russo on the Bay Glidepath template if empty
        const defaultRussoGlidepath = [
          { initiative: "ECM: Kitchen and Laundry Fans and Controls. Upgrades designed to improve reliability while reducing energy costs.", status: "ECM study shows promise of savings from change out of fans in concert with new controls integrated to SEAP BAS", plan: "Decide on budgeting for these fan and controls projects.", stepsToComplete: "1) Engage ONIT in engineering and design of systems; 2) Apply for Rebates; 3) Purchase equipment and install", benefitVsCost: "Reduced Energy Costs with initial outlay of $37K. Many of these fans are nearing the end of their life expectancy and will need replacement. 12-48 month return on investment.", cashGeneration: "105000", comments: "See ECM documentation for more details." },
          { initiative: "ECM: Pumps. Upgrades to improve reliability while reducing energy costs.", status: "ECM study shows promise of savings from change out of pumps in concert with new controls integrated to SEAP BAS", plan: "Decide on budgeting for these fan and controls projects.", stepsToComplete: "1) Engage ONIT in engineering and design of systems; 2) Apply for Rebates; 3) Purchase equipment and install", benefitVsCost: "12 month return on investment. 15 year life.", cashGeneration: "638000", comments: "See ECM documentation for more details." },
          { initiative: "ECM: Lighting Control. Upgrades to reduced energy costs.", status: "35% Installation completed", plan: "Plan to continue installing controls throughout the 225 lighting zones.", stepsToComplete: "1) Continue purchasing specified controls; 2) Install controls as time permits in order to meet LL88 requirements.", benefitVsCost: "6 to 72 month return on investment. 15 year life.", cashGeneration: "190000", comments: "See ECM documentation for more details. LL88 compliance requirement." },
          { initiative: "ECM: Plug Loads. Upgrades to reduce energy costs.", status: "Computer settings changed to deploy reduced energy usage. Partial Plug Load Switches Purchased with mobile app proven to work for existing WIFI", plan: "Plan to continue installing Plug Load Controls throughout facility for Identified items such as bug lights and small refrigeration.", stepsToComplete: "1) Continue purchasing specified controls; 2) Install controls and program for scheduled use.", benefitVsCost: "6-24 month return on investment. 15 year life.", cashGeneration: "=9600*15", comments: "See ECM documentation for more details." },
          { initiative: "ECM: Laundry Center - Work procedures to reduce energy, chemicals.", status: "Work process procedure was updated. ONIT developed Laundry Center Application for use by workers - Presently running on a server available as a prototype.", plan: "Plan to engage ONIT to enhance the prototype application to include workflow of machines to dryers, and integration of machine kWh from RTEM installation as part of BAS. ROTB to engage ECOLAB to test new chemicals that hold promise of 30% reduction of chemicals and energy.", stepsToComplete: "1) Engage ONIT to harden existing working prototype for use on local computer; 2) Finish wiring and programming of computing devices for machine monitoring and integrate to ONIT SEAP", benefitVsCost: "12 month ROI assuming initial $20,000 on top of NYSERDA's present investment. Ten year life.", cashGeneration: "200000", comments: "See Report on Laundry and AI software prototype. CTQs, Workflow efficiency" },
          { initiative: "ECM: EMIS-ASO (Energy Management Information System-Automated System Optimization) with FDD (Fault Detection and Diagnostics). Building Automation System (BAS) controllers for control and monitoring of all systems. Upgrade in AI based intelligence holds promise of reducing peak load, reducing maintenance and emergency repair costs, reducing energy usage, improving customer comfort.", status: "ONIT created drawings for wiring OsEM Task 8; ROTB ordered many parts; ROTB ran 50% of Wires; ONIT programmed twelve CT devices to ONIT SEAP that are operational as hardened proof of IPMVP M&V and kWh Energy and NOAA weather forecast data capture, use, and energy saving reports. ONIT created through programming detailed space and event calendars for ROTB to enable seamless transfer of Salesforce data to ONIT SEAP - APIs are ready. SEAP BAS working for two walk-in refrigeration systems and lighting systems. Virtual Points for Fault Detection and M&V operational for these systems.", plan: "Plan to have BMS specifications and points and JACE hardware available for ONIT to then program a computing device to communicate with JACE and ONIT SEAP. Create tailored FDD equipment templates that are centric to equipment and business needs. ROTB has proposed to NYSERDA training project to train personnel with perpetual training abilities as part of its glidepath to integrating this AI based technology into its operational group.", stepsToComplete: "Complete installing existing wiring package; Steps: 1)ONIT to be engaged to further design, program, including engaged to integrate BMS to SEAP EMIS-ASO and 2) ROTB or contractor to install EMIS points for HVAC RTUs, Refrigeration, Identified ECMs.", benefitVsCost: "Predictive failure of equipment. Reduced energy usage with improved comfort. KPIs of energy, process, and space utilization. Identified $143k per year savings potential at a cost of $70k in design, adaptation programming, and ROTB installation. 15 year life.", cashGeneration: "=143000*15", comments: "See ECM documentation for more details." },
          { initiative: "Training - For Productivity improvement, safety improvement, energy cost reduction, maintenance cost reductions.", status: "Detailed videos on the maintenance of equipment were created and available in a file folder. ONIT developed a Training application for use by workers. Presently running on a server available as a production system for use by ROTB. ROTB Youtube channel created with modest upload of developed training videos. Procedures for key equipment have been captured for use in Training. Alpha prototype of pdf O&M to workflow created.", plan: "Plan - Given the present state of tools, creating workflows, capturing additional O&M manuals for equipment not reviewed (HVAC), create additional training content makes sense to continue given the framework provided. Created a proposal to NYSERDA to continue on Training content creation based on the success of the existing effort.", stepsToComplete: "1) Create more videos, 2) Create AI based training. 3) integrate to YouTube Channel set up for ROTB; 4) Utilize the training application that is was created with the intent of ROTB usage.", benefitVsCost: "Reduced Labor Costs for training. Higher productivity from trained workforce. Improved understanding of equipment operations and maintenance. With a complete training program, labor is known to be reduced by 20%-25% first year improvement in productivity and savings. Increased growth of 10-15% is expected from refresher training. Given $400,000 per year in worker salaries, that equates to $80,000 improvement in productivity and energy savings, conservatively over a ten year training initiative.", cashGeneration: "800000", comments: "See Evidence from Quantitative Studies on Training Programs in Hospitality Report. https://x.com/i/grok/share/iqYkIsbnCy0EncMxzBTCImfCR" }
        ];
        replace(defaultRussoGlidepath as any);
      }
    }
  }, [proposal, replace]);

  const saveMutation = useMutation({
    mutationFn: async (values: GlidepathFormValues) => {
      const res = await apiRequest("PUT", `/api/osem/${id}`, {
        ...proposal,
        glidepathEntries: values.glidepathEntries
      });
      return await res.json();
    },
    onSuccess: () => {
      toast({ title: "Glidepath Saved", description: "Glidepath initiatives saved successfully." });
      queryClient.invalidateQueries({ queryKey: [`/api/osem/${id}`] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: `Failed to save glidepath plan: ${err.message}`, variant: "destructive" });
    }
  });

  const exportExcel = () => {
    const entries = form.getValues("glidepathEntries") || [];
    const companyName = proposal?.companyName || "Company";
    
    const wsData: any[][] = [];
    wsData.push([
      "#",
      "Savings/Growth/Lower Risk Initiative",
      "Status",
      "Glidepath - Plan",
      "Steps to Complete",
      "Benefit versus Cost",
      "Cash Generation over Life",
      "Comments"
    ]);

    entries.forEach((item, index) => {
      let cashVal: any = item.cashGeneration;
      if (typeof cashVal === "string" && cashVal.startsWith("=")) {
        cashVal = { f: cashVal.substring(1) };
      } else {
        const num = Number(cashVal);
        if (!isNaN(num)) cashVal = num;
      }

      wsData.push([
        index + 1,
        item.initiative,
        item.status,
        item.plan,
        item.stepsToComplete,
        item.benefitVsCost,
        cashVal,
        item.comments
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 6 },   // Item #
      { wch: 40 },  // Initiative
      { wch: 45 },  // Status
      { wch: 45 },  // Plan
      { wch: 45 },  // Steps to Complete
      { wch: 45 },  // Benefit vs Cost
      { wch: 18 },  // Cash Generation
      { wch: 30 }   // Comments
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${companyName.replace(/\s+/g, "_")}_Glidepath.xlsx`);
  };

  const handleEcmLink = (index: number, ecmNumStr: string) => {
    if (ecmNumStr === "custom") {
      form.setValue(`glidepathEntries.${index}.ecmNumber`, "");
      return;
    }

    const ecms = proposal?.ecmFindings || [];
    const matched = ecms.find((e: any) => e.ecmNumber === ecmNumStr);
    if (matched) {
      form.setValue(`glidepathEntries.${index}.ecmNumber`, matched.ecmNumber);
      form.setValue(`glidepathEntries.${index}.initiative`, `ECM: ${matched.description}. Upgrades to improve efficiency and reduce operational costs.`);
      form.setValue(`glidepathEntries.${index}.benefitVsCost`, `${matched.simplePayback ? matched.simplePayback + ' years simple payback.' : 'Cost savings program.'} Total cost $${matched.projectCost.toLocaleString()}.`);
      form.setValue(`glidepathEntries.${index}.cashGeneration`, matched.costSavings ? String(matched.costSavings * 15) : "0");
      form.setValue(`glidepathEntries.${index}.comments`, `Linked to walkthrough audit ECM #${matched.ecmNumber}.`);
    }
  };

  if (isLoading) {
    return (
      <AppShell fluid>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  const ecms = proposal?.ecmFindings || [];

  return (
    <AppShell fluid>
      <div className="w-full max-w-[98%] px-4 mx-auto space-y-6 pb-12">
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setLocation(`/osem-onboarding/${id}`)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Task 10: Glidepath Transition Plan</h1>
              <p className="text-muted-foreground text-sm">Link walkthrough ECMs and detail continuous labor, training, and operational growth.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 no-print">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" /> Print to PDF
            </Button>
            <Button variant="outline" onClick={exportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Glidepath (.xlsx)
            </Button>
            <Button onClick={form.handleSubmit((v) => saveMutation.mutate(v))} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Plan
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Transition Initiatives</CardTitle>
                <CardDescription>
                  Configure the savings and operational initiatives that will continue into the facility's post-onboarding phase.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border rounded-lg overflow-x-auto bg-muted/5">
                  <div className="min-w-[1200px] divide-y divide-border">
                    <div className="grid grid-cols-[3rem_12rem_16rem_12rem_14rem_14rem_10rem_1fr_auto] gap-2 items-center p-3 text-xs font-semibold uppercase text-muted-foreground bg-muted/20">
                      <div>#</div>
                      <div>Link ECM</div>
                      <div>Initiative</div>
                      <div>Status</div>
                      <div>Plan</div>
                      <div>Steps</div>
                      <div>Benefit vs Cost</div>
                      <div>Cash Sav. (Life)</div>
                      <div>Comments</div>
                      <div className="w-8"></div>
                    </div>

                    <div className="divide-y divide-border">
                      {fields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-[3rem_12rem_16rem_12rem_14rem_14rem_10rem_1fr_auto] gap-2 items-start p-2 py-3 bg-background hover:bg-muted/10 transition-colors">
                          <div className="text-sm font-medium text-muted-foreground text-center pt-2">
                            {index + 1}
                          </div>
                          
                          {/* Link ECM Select */}
                          <div>
                            <Select
                              defaultValue={field.ecmNumber || "custom"}
                              onValueChange={(val) => handleEcmLink(index, val)}
                            >
                              <SelectTrigger className="h-9 text-xs">
                                <SelectValue placeholder="Custom Initiative" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="custom">Custom (Unlinked)</SelectItem>
                                {ecms.map((e: any) => (
                                  <SelectItem key={e.ecmNumber} value={e.ecmNumber}>
                                    ECM {e.ecmNumber}: {e.description}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Initiative Description */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.initiative`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Status */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.status`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Plan */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.plan`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Steps to Complete */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.stepsToComplete`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Benefit vs Cost */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.benefitVsCost`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Cash Generation */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.cashGeneration`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Input className="h-9 text-xs" {...field} placeholder="e.g. 150000 or =9600*15" /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Comments */}
                          <FormField
                            control={form.control}
                            name={`glidepathEntries.${index}.comments`}
                            render={({ field }) => (
                              <FormItem className="space-y-0">
                                <FormControl><Textarea className="min-h-[70px] text-xs resize-none" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Remove row */}
                          <div className="pt-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => remove(index)}
                              className="text-destructive h-8 w-8 hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => append({ ecmNumber: "", initiative: "", status: "", plan: "", stepsToComplete: "", benefitVsCost: "", cashGeneration: "0", comments: "" })}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Initiative (e.g. Training)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
    </AppShell>
  );
}
